import { runWithRetry } from '@wix/seo-common-core';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { inaccessibleComponentsSessionAtom, inaccessibleComponentsSessionPagesAtom } from '../../../../atoms/session-atoms';
import { useA11yWizardAdapter } from '../../../../hooks/use-a11y-wizard-adapter';
import { getCompRefType } from '../../../../utils/get-comp-ref-type';
export const useSyncSessionComponents = (failedElements, pageId) => {
  const setSessionComponents = useSetAtom(inaccessibleComponentsSessionAtom);
  const setInaccessibleComponentsPages = useSetAtom(inaccessibleComponentsSessionPagesAtom);
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();

  // Workaround: getComponent may fail on first attempt but succeed on retry.
  const getComponent = useCallback(async (id, breakpointId) => {
    try {
      return await runWithRetry(async () => {
        const component = a11yWizardAdapter.getComponent({
          id,
          type: getCompRefType(editorHost, breakpointId)
        });
        if (!component) {
          throw new Error(`Component ${id} is null/undefined`);
        }
        return component;
      }, {
        delayInMilliseconds: 300,
        isExponentialBackoff: false,
        maxNumberOfAttempts: 2
      });
    } catch {
      return null;
    }
  }, [a11yWizardAdapter, editorHost]);
  useEffect(() => {
    setInaccessibleComponentsPages(prev => {
      if (!prev.includes(pageId)) {
        return [...prev, pageId];
      }
      return prev;
    });
  }, [pageId, setInaccessibleComponentsPages]);
  useEffect(() => {
    const updateSessionComponents = async () => {
      let currentPageComponents = [];
      setSessionComponents(prev => {
        currentPageComponents = prev[pageId] || [];
        return prev;
      });
      const currentPageComponentsIds = new Set(currentPageComponents.map(el => el.id));
      const failedElementsIds = new Set(failedElements.map(el => el.id));
      const updatedElements = currentPageComponents.map(sessionComponent => ({
        ...sessionComponent,
        isFixed: !failedElementsIds.has(sessionComponent.id)
      }));
      const newElementsPromises = failedElements.filter(_ref => {
        let {
          id
        } = _ref;
        return !currentPageComponentsIds.has(id);
      }).map(async element => {
        const component = await getComponent(element.id, element.breakpointId);
        return {
          ...element,
          ...(component || {})
        };
      });
      const newElements = await Promise.all(newElementsPromises);
      setSessionComponents(prev => {
        const isSameState = updatedElements.length === currentPageComponents.length && updatedElements.every((el, index) => el.id === currentPageComponents[index].id && el.isFixed === currentPageComponents[index].isFixed) && newElements.length === 0;
        if (isSameState) {
          return prev;
        }
        return {
          ...prev,
          [pageId]: [...updatedElements, ...newElements]
        };
      });
    };
    updateSessionComponents();
  }, [failedElements, getComponent, pageId, setSessionComponents]);
};
//# sourceMappingURL=use-sync-session-components.js.map